import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { removePendingTrade } from '../../../../redux/slices/pending';
import ReducerConstants from '../../../../_shared/constants/reducers';
import { httpErrorCodes } from '../../../../_shared/constants/errorCodes';
import Alert from '../../../../_shared/components/Alert';
import copyToClipBoard from '../../../../_shared/utils/copyToClipBoard';
import TradeListService from '../../../../services/TradeListService';
import TradeService from '../../../../services/TradeService';
import UserService from '../../../../services/UserService';
import ConfirmModal from '../../../../_shared/components/ConfirmModal';
import MixpanelConfig from '../../../../_shared/constants/mixpanelConfig';
import { BidSource } from '../../../../_shared/types/auction';
import ROUTES from '../../../Routes/constants';
import NegotiationState from '../../../../_shared/constants/negotiationState';
import getQuickBidIncrements from '../../../../_shared/utils/getQuickBidIncrements';
import {
  StyledBody,
  StyledFormSection,
  StyledInput,
  StyledInputSection,
  StyledSelect,
  StyledSelectSection
} from './Styles';
import { StyledLabel } from '../../../../_shared/components/Select/Styles';
import SendToLostContainer from '../../../ModalButton/NegotiationButton/SendToLostContainer';
import NegotiationModalContainer from '../../../Modal/NegotiationModalContainer';
import {
  TradeState,
  TradeStateNewName,
  AllowedStateChangeProperties,
  TradeContext
} from '../../../../_shared/types/trade';

import KebabMenu from '../../../../_shared/components/KebabMenu';
import SendToClosingModal from '../../../Modal/SendToClosingModal';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties
} from '../../../../_shared/hooks/useAnalytics';
import useFeatureFlags from '../../../../_shared/hooks/useFeatureFlags';
import FEATURE_FLAGS from '../../../../_shared/constants/featureFlags';
import SegmentConfig from '../../../../_shared/constants/segmentConfig';
import TRToast from '../../../../_shared/helpers/TRToast';

const KebabMenuSeller = ({
  vin,
  tradeId,
  sellingNetworks,
  isOpenNetwork,
  tradeSummary,
  setPendingCardFooter,
  isNoNegotiationsYet,
  isOmp = false
}) => {
  const track = useAnalytics();
  const dispatch = useDispatch();
  const intl = useIntl();
  const isRemoveOpenToDgnEnabled = useFeatureFlags(
    FEATURE_FLAGS.IS_REMOVE_OPEN_TO_DGN_ENABLED
  );
  const tradeListService = TradeListService.get();
  const tradeService = TradeService.get();
  const userService = UserService.get();
  const isVdp = window.location.href.includes(ROUTES.TRADE_URI);
  const eventSource = isVdp ? BidSource.VDP_OVERLAY : BidSource.LIST_VIEW;
  const [configurations, user] = useSelector(
    state => [
      state[ReducerConstants.AUTH]?.system?.configurations,
      state[ReducerConstants.AUTH]?.user
    ],
    shallowEqual
  );
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSendToLostModalOpen, setIsSendToLostModalOpen] = useState(false);
  const [isSendToClosingModalOpen, setIsSendToClosingModalOpen] = useState(
    false
  );
  const [isNegotiationModalOpen, setIsNegotiationModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [markLostMessage, setMarkLostMessage] = useState(null);
  const dealerGroups = sellingNetworks?.reduce(
    (acc, network) => {
      if (network && network?.isDealerGroupNetwork) {
        acc.push({
          id: network.id,
          value: network.id,
          label: network.displayName
        });
      }
      return acc;
    },
    [
      {
        id: 'select-network',
        value: '0',
        label: intl.formatMessage({ id: 'list.controls.selectNetwork' }),
        disabled: true
      }
    ]
  );
  const selectedDgnNetwork = useRef(
    sellingNetworks ? dealerGroups[0]?.value : 0
  );
  const startingBidAmount = useRef(tradeSummary?.topBid);
  const [confirmModalLogic, setConfirmModalLogic] = useState([]);
  const userHasDgn = sellingNetworks?.length > 1;
  const canLaunchToDgn =
    userHasDgn &&
    isOpenNetwork &&
    !tradeSummary?.associatedTradeId &&
    !isRemoveOpenToDgnEnabled;
  const isInternalTrade = tradeSummary?.isInternalTrade;
  const openNetworkAssociatedTradeId = tradeSummary?.associatedTradeId;

  const isAssociatedTradeActive = stateNum => stateNum === TradeState.active;
  const isAssociatedTradePending = stateNum => stateNum === TradeState.pending;
  const isAssociatedTradeLost = stateNum => stateNum === TradeState.lost;

  const handleParkTrade = async () => {
    setIsLoading(true);
    const park = await tradeListService.moveToTradeState(
      tradeId,
      TradeState.pending,
      TradeState.parked
    );
    setIsConfirmModalOpen(false);
    if (!park?.isError) {
      const associatedTradeState = tradeService
        .getAssociatedTradeState(openNetworkAssociatedTradeId)
        .then(data => data);

      associatedTradeState.then(currentTradeState => {
        if (isAssociatedTradeLost(currentTradeState)) {
          dispatch(removePendingTrade(openNetworkAssociatedTradeId));
        }
      });
      TRToast(
        <Alert
          variant="success"
          messages={[
            <FormattedMessage id="list.modal.pending.park.confirm.success" />
          ]}
        />
      );

      track(SegmentConfig.names.pendingParkVehicleConfirmed, {
        ...getVinBasicProperties(tradeSummary),
        ...getSellerProperties(tradeSummary),
        event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
          ? SegmentConfig.eventStateType.sellPending
          : SegmentConfig.eventStateType.buyPending
      });
      track(MixpanelConfig.names.parkVehicleFromPendingConfirm, {
        source: eventSource,
        trade_id: tradeSummary?.id,
        auction_id: tradeSummary?.auctionId
      });

      if (isVdp) {
        setTimeout(() => window.location.reload(), 1000);
      } else {
        dispatch(removePendingTrade(tradeId));
      }
    } else if (
      park?.errorCode === httpErrorCodes.CANNOT_PARK_DGN_IN_ACTIVE &&
      park?.errorMessage
    ) {
      TRToast(<Alert variant="error" messages={[park.errorMessage]} />);
    } else {
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage id="list.modal.pending.park.confirm.error" />
          ]}
        />
      );
    }
    setIsLoading(false);
  };

  const handleMarkLost = async () => {
    setIsLoading(true);
    const markLost = await tradeListService.moveToTradeState(
      tradeSummary?.id,
      TradeState.pending,
      TradeState.lost
    );
    if (!markLost.isError) {
      TRToast(
        <Alert
          variant="success"
          messages={[
            <FormattedMessage id="list.modal.pending.moveToLost.confirm.success" />
          ]}
        />
      );
      track(SegmentConfig.names.pendingSendToNotSoldConfirmed, {
        ...getVinBasicProperties(tradeSummary),
        ...getSellerProperties(tradeSummary),
        event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
          ? SegmentConfig.eventStateType.sellPending
          : SegmentConfig.eventStateType.buyPending
      });
      track(MixpanelConfig.names.sendToLostFromPendingConfirm, {
        source: eventSource,
        trade_id: tradeSummary?.id,
        auction_id: tradeSummary?.auctionId
      });

      if (isVdp) {
        setTimeout(() => window.location.reload(), 1000);
      } else {
        dispatch(removePendingTrade(tradeSummary?.id));
      }
    } else {
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage id="list.modal.pending.moveToLost.confirm.error" />
          ]}
        />
      );
    }
    setIsLoading(false);
  };

  const parkTrade = () => {
    if (isInternalTrade) {
      setConfirmModalLogic([
        {
          msg: <FormattedMessage id="list.modal.dgn.park.error.message" />,
          title: <FormattedMessage id="list.modal.dgn.park.error.title" />,
          confirmLabel: <FormattedMessage id="modal.ok" />,
          onConfirm: () => () => setIsConfirmModalOpen(false)
        }
      ]);

      track(SegmentConfig.names.pendingErrorMessageShown, {
        ...getVinBasicProperties(tradeSummary),
        ...getSellerProperties(tradeSummary),
        event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
          ? SegmentConfig.eventStateType.sellPending
          : SegmentConfig.eventStateType.buyPending,
        pending_error_type: SegmentConfig.errorType.cannotParkOpenTrade
      });
    } else {
      setConfirmModalLogic([
        {
          msg:
            openNetworkAssociatedTradeId !== null ? (
              <FormattedMessage id="list.modal.pending.openNetwork.park.confirm.message" />
            ) : (
              <FormattedMessage id="list.modal.pending.park.confirm.message" />
            ),
          title:
            openNetworkAssociatedTradeId !== null ? (
              <FormattedMessage id="list.modal.pending.openNetwork.park.confirm.title" />
            ) : (
              <FormattedMessage id="list.modal.pending.park.confirm.title" />
            ),
          confirmLabel: (
            <FormattedMessage id="list.modal.pending.park.confirm.confirm" />
          ),
          cancelLabel: <FormattedMessage id="modal.cancel" />,
          onCancel: () => () => {
            setIsConfirmModalOpen(false);
          },
          onConfirm: () => handleParkTrade
        }
      ]);

      track(SegmentConfig.names.pendingParkVehicleClicked, {
        ...getVinBasicProperties(tradeSummary),
        ...getSellerProperties(tradeSummary),
        event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
          ? SegmentConfig.eventStateType.sellPending
          : SegmentConfig.eventStateType.buyPending,
        event_source_type: window.location.href.includes(ROUTES.TRADE_URI)
          ? SegmentConfig.eventSourceType.pendingVDP
          : SegmentConfig.eventSourceType.pendingListView
      });
      track(MixpanelConfig.names.parkVehicleFromPending, {
        source: eventSource,
        trade_id: tradeSummary?.id,
        auction_id: tradeSummary?.auctionId
      });
    }
    setIsConfirmModalOpen(true);
  };

  const handleNetworkChange = e => {
    selectedDgnNetwork.current = e;
  };

  const handleBidAmountChange = e => {
    startingBidAmount.current = parseInt(e.target.value, 10);
  };

  const modalMessage = () => {
    return (
      <>
        <StyledBody>
          <FormattedMessage id="list.modal.pending.auctionToDealerGroup.message" />
        </StyledBody>
        <StyledFormSection>
          <StyledSelectSection>
            <StyledLabel>
              <FormattedMessage id="list.modal.pending.auctionToDealerGroup.selectNetwork.label" />
            </StyledLabel>
            <StyledSelect
              testId="pending-dealerGroup"
              className="pending-dealerGroup"
              options={dealerGroups}
              defaultLabel={selectedDgnNetwork?.current}
              onChange={handleNetworkChange}
            />
          </StyledSelectSection>
          <StyledInputSection>
            <StyledLabel>
              <FormattedMessage id="list.modal.pending.auctionToDealerGroup.startingBidAmount.label" />
            </StyledLabel>
            <StyledInput
              data-testid="start-bid-amount"
              type="number"
              defaultValue={startingBidAmount?.current}
              min={0}
              step={getQuickBidIncrements(startingBidAmount, configurations)}
              onChange={handleBidAmountChange}
            />
          </StyledInputSection>
        </StyledFormSection>
      </>
    );
  };

  const handleLaunchToDgn = async () => {
    const isFormValid =
      selectedDgnNetwork?.current !== '0' && startingBidAmount?.current !== 0;

    if (isFormValid) {
      try {
        setIsLoading(true);
        const launchToActive = await tradeListService.launchToNetwork(
          tradeId,
          selectedDgnNetwork?.current,
          startingBidAmount?.current
        );
        setIsConfirmModalOpen(false);

        if (launchToActive?.ok) {
          const selectedNetwork = sellingNetworks?.find(network => {
            return parseInt(network.id, 10) === selectedDgnNetwork?.current;
          })?.displayName;

          TRToast(
            <Alert
              variant="success"
              messages={[
                <FormattedMessage
                  id="list.modal.pending.auctionToDealerGroup.confirm.success"
                  values={{ dealerNetwork: selectedNetwork }}
                />
              ]}
            />
          );
          setPendingCardFooter(true);

          track(MixpanelConfig.names.auctionInDGNConfirm, {
            source: eventSource,
            trade_id: tradeSummary?.id,
            auction_id: tradeSummary?.auctionId,
            network_type: MixpanelConfig.networkType.internal,
            network_option: selectedNetwork,
            starting_price: startingBidAmount?.current
          });

          if (isVdp) {
            setTimeout(() => window.location.reload(), 1000);
          }
        } else if (tradeSummary?.topBid < startingBidAmount?.current) {
          TRToast(
            <Alert
              variant="error"
              messages={[
                <FormattedMessage id="list.modal.pending.auctionToDealerGroup.confirm.startBid.error" />
              ]}
            />
          );
        } else {
          TRToast(
            <Alert
              variant="error"
              messages={[
                <FormattedMessage id="list.modal.pending.auctionToDealerGroup.confirm.error" />
              ]}
            />
          );
        }
      } catch (error) {
        return error;
      }
    }
    setIsLoading(false);
    // eslint-disable-next-line require-atomic-updates
    selectedDgnNetwork.current = dealerGroups[0]?.value;
    // eslint-disable-next-line require-atomic-updates
    startingBidAmount.current = tradeSummary?.topBid;
    return null;
  };

  const cancelLaunchToDgn = () => {
    selectedDgnNetwork.current = dealerGroups[0]?.value;
    startingBidAmount.current = tradeSummary?.topBid;
    setIsConfirmModalOpen(false);
  };

  const auctionInDGN = () => {
    if (
      [NegotiationState.NO_NEGOTIATION, NegotiationState.BUYER_OFFER].includes(
        tradeSummary?.negotiationState
      )
    ) {
      setConfirmModalLogic([
        {
          msg: modalMessage(),
          title: (
            <FormattedMessage id="list.modal.pending.auctionToDealerGroup.title" />
          ),
          confirmLabel: (
            <FormattedMessage id="list.modal.pending.auctionToDealerGroup.confirm.button" />
          ),
          cancelLabel: <FormattedMessage id="modal.cancel" />,
          onCancel: () => cancelLaunchToDgn,
          onConfirm: () => handleLaunchToDgn
        }
      ]);

      track(SegmentConfig.names.pendingAuctionInDGNClicked, {
        ...getVinBasicProperties(tradeSummary),
        ...getSellerProperties(tradeSummary),
        event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
          ? SegmentConfig.eventStateType.sellPending
          : SegmentConfig.eventStateType.buyPending
      });
    } else {
      setConfirmModalLogic([
        {
          title: (
            <FormattedMessage id="list.modal.pending.auctionToDealerGroup.in.nego.title" />
          ),
          msg: (
            <FormattedMessage id="list.modal.pending.auctionToDealerGroup.in.nego.message" />
          ),
          confirmLabel: <FormattedMessage id="modal.ok" />,
          onConfirm: () => () => setIsConfirmModalOpen(false)
        }
      ]);

      track(SegmentConfig.names.pendingErrorMessageShown, {
        ...getVinBasicProperties(tradeSummary),
        ...getSellerProperties(tradeSummary),
        event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
          ? SegmentConfig.eventStateType.sellPending
          : SegmentConfig.eventStateType.buyPending,
        pending_error_type: SegmentConfig.errorType.cannotStartDgnNego
      });
      track(MixpanelConfig.names.sellerCannotStartDGNNegotiationError, {
        source: eventSource,
        trade_id: tradeSummary?.id,
        auction_id: tradeSummary?.auctionId
      });
    }
    setIsConfirmModalOpen(true);

    track(MixpanelConfig.names.auctionInDGN, {
      source: eventSource,
      trade_id: tradeSummary?.id,
      auction_id: tradeSummary?.auctionId
    });
  };

  const sendTradeToClosing = async actualCashValue => {
    const moveSuccess = await tradeListService.moveToClosing(
      tradeSummary?.id,
      TradeState.pending,
      [{ key: AllowedStateChangeProperties.ACV, value: actualCashValue }]
    );

    TRToast(
      <Alert
        variant={moveSuccess && !moveSuccess.isError ? 'success' : 'error'}
        messages={[
          <FormattedMessage
            id={
              moveSuccess && !moveSuccess.isError
                ? 'modal.decision.sendToClosing.success'
                : moveSuccess.errorMessage
            }
          />
        ]}
      />
    );

    if (moveSuccess && !moveSuccess.isError) {
      track(SegmentConfig.names.pendingSendToClosingConfirmed, {
        ...getVinBasicProperties(tradeSummary),
        ...getSellerProperties(tradeSummary),
        event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
          ? SegmentConfig.eventStateType.sellPending
          : SegmentConfig.eventStateType.buyPending
      });
      track(MixpanelConfig.names.sendToClosingFromPendingConfirm, {
        source: eventSource,
        trade_id: tradeSummary?.id,
        auction_id: tradeSummary?.auctionId
      });

      const isVdpPath = window.location.pathname.startsWith(ROUTES.TRADE_URI);
      if (isVdpPath) {
        setTimeout(() => window.location.reload(), 1000);
      } else {
        dispatch(removePendingTrade(tradeSummary?.id));
      }
    }
  };

  const onSendToClosing = async actualCashValue => {
    setIsLoading(true);
    await sendTradeToClosing(actualCashValue);
    setIsSendToClosingModalOpen(false);
    setIsLoading(false);
  };

  const sendToLost = () => {
    const associatedTradeState = tradeService
      .getAssociatedTradeState(openNetworkAssociatedTradeId)
      .then(data => data);
    associatedTradeState.then(currentTradeState => {
      if (isInternalTrade) {
        setMarkLostMessage(
          <FormattedMessage id="list.modal.dgn.markLost.message" />
        );
      } else if (
        isAssociatedTradePending(currentTradeState) ||
        isAssociatedTradeActive(currentTradeState)
      ) {
        setMarkLostMessage(
          <FormattedMessage id="list.modal.open.markLost.message" />
        );
      } else {
        setMarkLostMessage(
          <FormattedMessage id="list.modal.pending.moveToLost.confirm.message" />
        );
      }
      setIsSendToLostModalOpen(true);

      track(SegmentConfig.names.pendingSendToNotSoldClicked, {
        ...getVinBasicProperties(tradeSummary),
        ...getSellerProperties(tradeSummary),
        event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
          ? SegmentConfig.eventStateType.sellPending
          : SegmentConfig.eventStateType.buyPending,
        event_source_type: window.location.href.includes(ROUTES.TRADE_URI)
          ? SegmentConfig.eventSourceType.pendingVDP
          : SegmentConfig.eventSourceType.pendingListView
      });
      track(MixpanelConfig.names.sendToLostFromPending, {
        source: eventSource,
        trade_id: tradeSummary?.id,
        auction_id: tradeSummary?.auctionId
      });
    });
  };

  const sendToClosing = () => {
    setIsSendToClosingModalOpen(true);

    track(SegmentConfig.names.pendingSendToClosingClicked, {
      ...getVinBasicProperties(tradeSummary),
      ...getSellerProperties(tradeSummary),
      event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
        ? SegmentConfig.eventStateType.sellPending
        : SegmentConfig.eventStateType.buyPending,
      event_source_type: window.location.href.includes(ROUTES.TRADE_URI)
        ? SegmentConfig.eventSourceType.pendingVDP
        : SegmentConfig.eventSourceType.pendingListView
    });
    track(MixpanelConfig.names.sendToClosingFromPending, {
      source: eventSource,
      trade_id: tradeSummary?.id,
      auction_id: tradeSummary?.auctionId
    });
  };

  const onNegotiateClick = () => {
    setIsConfirmModalOpen(false);
    setIsNegotiationModalOpen(true);
  };

  const encourageToNegotiate = actionLabel => {
    setConfirmModalLogic([
      {
        msg: (
          <FormattedMessage
            id="negotiation.encourage.modal.message"
            values={{ br: <br /> }}
          />
        ),
        title: (
          <FormattedMessage
            id="negotiation.encourage.modal.heading"
            values={{ actionLabel }}
          />
        ),
        confirmLabel: (
          <FormattedMessage id="vdp.pending.seller.button.startNegotiation" />
        ),
        cancelLabel: actionLabel,
        onCancel: () => async () => {
          if (
            actionLabel ===
            intl.formatMessage({
              id: 'negotiation.send.to.parked'
            })
          ) {
            await handleParkTrade();
          } else if (
            actionLabel ===
            intl.formatMessage({
              id: 'negotiation.send.to.closing'
            })
          ) {
            await sendTradeToClosing(tradeSummary?.topBid);
          } else if (
            actionLabel ===
            intl.formatMessage({
              id: 'negotiation.send.to.lost'
            })
          ) {
            await handleMarkLost();
          }
        },
        onConfirm: () => () => {
          onNegotiateClick();
        },
        onClickAway: () => () => setIsConfirmModalOpen(false)
      }
    ]);

    track(SegmentConfig.names.pendingUnusedProxyAlertShown, {
      ...getVinBasicProperties(tradeSummary),
      ...getSellerProperties(tradeSummary),
      event_state_type: userService.isSeller(user, tradeSummary?.dealer?.id)
        ? SegmentConfig.eventStateType.sellPending
        : SegmentConfig.eventStateType.buyPending
    });

    track(MixpanelConfig.names.sellerUnusedProxyModalShown, {
      trade_id: tradeSummary?.id,
      auction_id: tradeSummary?.auctionId,
      user_type: userService.isSeller(user, tradeSummary?.dealer?.id)
        ? MixpanelConfig.userType.seller
        : MixpanelConfig.userType.buyer,
      max_bid_in_auction: tradeSummary?.topBid
    });

    setIsConfirmModalOpen(true);
  };

  const onCloseNegotiationModal = () => {
    setIsNegotiationModalOpen(false);
  };

  const onCloseSendToLostModal = () => {
    setIsSendToLostModalOpen(false);
  };
  const onCloseSendToClosingModal = () => {
    setIsSendToClosingModalOpen(false);
  };

  const copyVin = e => {
    e.stopPropagation();
    copyToClipBoard(vin);
    TRToast(
      <Alert
        variant="information"
        messages={[<FormattedMessage id="vdp.success.vinCopied" />]}
      />
    );

    track(MixpanelConfig.names.copyVinToClipBoard, {
      source:
        eventSource === BidSource.VDP_OVERLAY
          ? MixpanelConfig.source.menu
          : eventSource,
      trade_id: tradeSummary?.id,
      auction_id: tradeSummary?.auctionId,
      trade_state: TradeStateNewName[tradeSummary?.tradeState]
    });
  };

  const kebabActions = () => {
    const isUnifiedVDPEnabled = ['beta', 'on'].includes(
      window?.omp_splits?.treatments?.feature_omp_appraisals_bundle?.treatment
    );

    return [
      {
        label: intl.formatMessage({
          id: 'tr5.controls.sendToClosing'
        }),
        onClick:
          tradeSummary?.unusedProxyExists && isNoNegotiationsYet
            ? () =>
                encourageToNegotiate(
                  intl.formatMessage({
                    id: 'negotiation.send.to.closing'
                  })
                )
            : sendToClosing
      },
      {
        label: intl.formatMessage({
          id: 'tr5.controls.parkVehicle'
        }),
        onClick:
          tradeSummary?.unusedProxyExists && isNoNegotiationsYet
            ? () =>
                encourageToNegotiate(
                  intl.formatMessage({
                    id: 'negotiation.send.to.parked'
                  })
                )
            : parkTrade
      },
      {
        label: intl.formatMessage({
          id: 'tr5.controls.auctionInDGN'
        }),
        onClick: auctionInDGN,
        hide: !canLaunchToDgn
      },
      !isUnifiedVDPEnabled
        ? {
            label: intl.formatMessage({
              id: 'tr5.controls.sendToLost'
            }),
            onClick:
              tradeSummary?.unusedProxyExists && isNoNegotiationsYet
                ? () =>
                    encourageToNegotiate(
                      intl.formatMessage({
                        id: 'negotiation.send.to.lost'
                      })
                    )
                : sendToLost
          }
        : undefined,
      {
        label: `${intl.formatMessage({
          id: 'vdp.overview.copyVin'
        })} ${vin}`,
        onClick: copyVin,
        isCopy: true
      }
    ].filter(item => item !== undefined);
  };

  return (
    <>
      <KebabMenu actions={kebabActions()} />

      <SendToLostContainer
        trade={tradeSummary}
        isOpen={isSendToLostModalOpen}
        markLostMessage={markLostMessage}
        onClose={onCloseSendToLostModal}
      />
      <SendToClosingModal
        modalIsOpen={isSendToClosingModalOpen}
        onSendToClosing={onSendToClosing}
        onClose={onCloseSendToClosingModal}
        topBid={tradeSummary?.topBid}
        associatedTrade={tradeSummary?.associatedTrade}
        associatedTradeId={tradeSummary?.associatedTradeId}
        isInternalTrade={isInternalTrade}
        isLoading={isLoading}
        trade={tradeSummary}
      />
      <ConfirmModal
        modalLogic={confirmModalLogic}
        isOpen={isConfirmModalOpen}
        isLoading={isLoading}
        loadingMsg={<FormattedMessage id="modal.confirmation.loading" />}
        width={540}
      />
      <NegotiationModalContainer
        trade={tradeSummary}
        modalIsOpen={isNegotiationModalOpen}
        tradeContext={
          tradeSummary?.isSelling ? TradeContext.Sell : TradeContext?.Buy
        }
        onClose={onCloseNegotiationModal}
        isOmp={isOmp}
      />
    </>
  );
};

export default KebabMenuSeller;
